import { useEffect } from "react";

const APP_HOST = /^https:\/\/[a-z0-9-]+\.domoapps\.prod9\.domo\.com$/;

function appOrigin(origin) {
  if (origin === "https://public.domo.com") return true;
  return APP_HOST.test(origin);
}

function frameHolds(win, source, depth) {
  if (!win || depth > 8) return false;
  if (win === source) return true;
  let n = 0;
  try {
    n = win.length;
  } catch (eN) {
    return false;
  }
  for (let i = 0; i < n; i++) {
    let child = null;
    try {
      child = win.frames[i];
    } catch (eC) {
      child = null;
    }
    if (child && frameHolds(child, source, depth + 1)) return true;
  }
  return false;
}

function frameOf(source) {
  const frames = document.querySelectorAll("iframe");
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    let win = null;
    try {
      win = frame.contentWindow;
    } catch (eW) {
      win = null;
    }
    if (!win) continue;
    if (win === source || frameHolds(win, source, 0)) return frame;
  }
  return null;
}

function fullElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

/**
 * Hook to handle full screen requests from Domo cards inside embedded iframes.
 * Responds to NMI_FULLSCREEN_HELLO and NMI_FULLSCREEN postMessages.
 */
export default function useDomoFullscreen() {
  useEffect(() => {
    let live = null;
    let stretchedFrame = null;
    let stretchedStyle = null;

    function tell(on) {
      if (!live || live.on === on) return;
      live.on = on;
      try {
        live.win.postMessage({ type: "NMI_FULLSCREEN_STATE", on, v: 1 }, live.origin);
      } catch (err) {}
    }

    function fallbackOff() {
      const frame = stretchedFrame;
      if (!frame) return;
      stretchedFrame = null;
      if (stretchedStyle === null) {
        frame.removeAttribute("style");
      } else {
        frame.setAttribute("style", stretchedStyle);
      }
      stretchedStyle = null;
      if (live && live.frame === frame) tell(false);
    }

    function fallbackOn(frame) {
      if (stretchedFrame === frame) return;
      fallbackOff();
      stretchedFrame = frame;
      stretchedStyle = frame.getAttribute("style");
      frame.style.position = "fixed";
      frame.style.top = "0";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.left = "0";
      frame.style.width = "100vw";
      frame.style.height = "100vh";
      frame.style.maxWidth = "none";
      frame.style.maxHeight = "none";
      frame.style.margin = "0";
      frame.style.border = "0";
      frame.style.zIndex = "2147483000";
      if (live && live.frame === frame) tell(true);
    }

    function expand(frame) {
      if (fullElement() === frame || stretchedFrame === frame) {
        tell(true);
        return;
      }
      const req = frame.requestFullscreen || frame.webkitRequestFullscreen;
      let pending = null;
      if (!req) {
        fallbackOn(frame);
        return;
      }
      try {
        pending = req.call(frame);
      } catch (err) {
        fallbackOn(frame);
        return;
      }
      if (pending && typeof pending.then === "function") {
        pending.then(
          () => {},
          () => {
            if (fullElement() !== frame) fallbackOn(frame);
          }
        );
      }
    }

    function collapse(frame) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (fullElement() === frame && exit) {
        try {
          const pending = exit.call(document);
          if (pending && typeof pending.catch === "function") {
            pending.catch(() => {});
          }
        } catch (err) {}
      }
      if (stretchedFrame === frame) fallbackOff();
    }

    function changed() {
      if (!live) return;
      if (fullElement() === live.frame) {
        tell(true);
      } else if (stretchedFrame !== live.frame) {
        tell(false);
      }
    }

    function refused() {
      if (live && fullElement() !== live.frame) {
        fallbackOn(live.frame);
      }
    }

    function handleMessage(ev) {
      const data = ev.data;
      if (!data || !appOrigin(ev.origin) || !ev.source) return;

      if (data.type === "NMI_FULLSCREEN_HELLO" && data.v === 1) {
        if (!frameOf(ev.source)) return;
        try {
          ev.source.postMessage({ type: "NMI_FULLSCREEN_ACK", v: 1 }, ev.origin);
        } catch (err) {}
        return;
      }

      if (data.type !== "NMI_FULLSCREEN" || data.v !== 1) return;

      const frame = frameOf(ev.source);
      if (!frame) return;

      if (data.on) {
        if (!live || live.frame !== frame || live.win !== ev.source) {
          live = { frame, win: ev.source, origin: ev.origin, on: false };
        }
        expand(frame);
      } else {
        collapse(frame);
      }
    }

    document.addEventListener("fullscreenchange", changed);
    document.addEventListener("webkitfullscreenchange", changed);
    document.addEventListener("fullscreenerror", refused);
    document.addEventListener("webkitfullscreenerror", refused);
    window.addEventListener("message", handleMessage);

    return () => {
      document.removeEventListener("fullscreenchange", changed);
      document.removeEventListener("webkitfullscreenchange", changed);
      document.removeEventListener("fullscreenerror", refused);
      document.removeEventListener("webkitfullscreenerror", refused);
      window.removeEventListener("message", handleMessage);
      if (stretchedFrame) {
        fallbackOff();
      }
    };
  }, []);
}
